import Checkbox from '@mui/joy/Checkbox';
import { ColumnDef, ColumnSizingState, Row } from '@tanstack/react-table';

import { Fragment, useEffect, useState } from 'react';
import { z } from 'zod';

import { baseUrl } from '@/utils/constants';
import { handleBasename } from '@/utils/handle-basename';
import { cn } from '@/utils/tailwind';

interface SelectColumnOptions<T> {
  variant?: 'virtual' | 'normal';
  alternateText?: (row: Row<T>) => string | undefined;
  checkboxesHighlighted?: boolean;
  lastSelectedId?: string | null;
  setLastSelectedId?: (id: string) => void;
  numCharsInAlternateTextBestGuess?: number;
  checkboxesInHoverState?: boolean;
}

export function getTableSelectColumnDef<T>(options?: SelectColumnOptions<T>) {
  const showAlternateText = !!options?.alternateText;
  let lastSelectedId = '';
  const columnWidth = options?.numCharsInAlternateTextBestGuess
    ? (options.numCharsInAlternateTextBestGuess + 1) * 10
    : options?.variant === 'virtual'
    ? 42
    : 32;

  function getRowRange(rows: Row<T>[], idA: string, idB: string) {
    const range: Row<T>[] = [];

    let foundStart = false;
    let foundEnd = false;

    for (let index = 0; index < rows.length; index += 1) {
      const row = rows[index];
      if (row.id === idA || row.id === idB) {
        if (foundStart) {
          foundEnd = true;
        } else {
          foundStart = true;
        }
      }

      if (foundStart) range.push(row);
      if (foundEnd) break;
    }

    if (!foundStart || !foundEnd) return [];
    return range;
  }

  return {
    id: 'select',
    header: ({ table }) => (
      <div
        className={cn(
          'absolute inset-0 flex cursor-pointer items-center',
          showAlternateText
            ? 'px-4'
            : options?.variant === 'virtual'
            ? 'px-4 pl-[17px]'
            : 'px-2'
        )}
        onClick={(e) => {
          e.stopPropagation();
          table.toggleAllPageRowsSelected(!table.getIsAllPageRowsSelected());
        }}
      >
        <Checkbox
          checked={table.getIsAllPageRowsSelected()}
          indeterminate={table.getIsSomePageRowsSelected()}
          size="sm"
        />
      </div>
    ),
    cell: ({ row, table }) => {
      const checkboxCell = (
        <div
          className={cn(
            'absolute inset-0 flex cursor-pointer items-center',
            showAlternateText || options?.variant === 'virtual'
              ? 'px-4'
              : 'px-2',
            showAlternateText && 'group-hover:opacity-100',
            showAlternateText && !row.getIsSelected() && 'opacity-0'
          )}
          onClick={(e) => {
            e.stopPropagation();

            const newValue = !row.getIsSelected();
            const target = e.shiftKey
              ? getRowRange(
                  table.getRowModel().rows,
                  row.id,
                  options?.lastSelectedId ?? lastSelectedId
                )
              : [];

            if (target.length === 0) target.push(row);

            target.forEach((row) => row.toggleSelected(newValue));
            lastSelectedId = row.id;
            options?.setLastSelectedId?.(lastSelectedId);
          }}
          data-testid={`checkbox-row-${row.index}`}
        >
          <Checkbox
            checked={row.getIsSelected()}
            size="sm"
            sx={{
              '& .MuiCheckbox-checkbox': {
                border: options?.checkboxesHighlighted
                  ? '1px solid #156FEE'
                  : undefined,
                boxShadow:
                  options?.checkboxesInHoverState && !row.getIsSelected()
                    ? '0px 0px 0px 4px var(--bg-brand-secondary), 0px 1px 2px 0px #1018280D'
                    : undefined,
                borderColor:
                  options?.checkboxesInHoverState && !row.getIsSelected()
                    ? 'var(--border-brand)'
                    : undefined,
              },
            }}
          />
        </div>
      );
      if (showAlternateText) {
        return (
          <Fragment>
            <div
              className={cn(
                'absolute inset-0 flex cursor-pointer items-center px-4 group-hover:opacity-0',
                !row.getIsSelected() ? 'opacity-100' : 'opacity-0'
              )}
            >
              {options?.alternateText?.(row)}
            </div>
            {checkboxCell}
          </Fragment>
        );
      }
      return checkboxCell;
    },
    enableSorting: false,
    enableHiding: false,
    enableResizing: false,
    size: columnWidth,
    meta: { clampSize: columnWidth, select: false },
  } satisfies ColumnDef<T>;
}

export function useDelayedLoading(isLoading: boolean) {
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    if (!isLoading) {
      setIsLoaded(false);
      return;
    }

    const timeout = setTimeout(() => {
      setIsLoaded(true);
    }, 600);

    return () => clearTimeout(timeout);
  }, [isLoading]);

  return isLoaded;
}

export function emulateNativeClickManual(
  target: string,
  event: {
    metaKey: boolean;
    altKey: boolean;
    ctrlKey: boolean;
    shiftKey: boolean;
  }
) {
  if (/^((?!chrome|android).)*safari/i.test(navigator.userAgent)) {
    return false;
  }

  if (event.metaKey || event.altKey || event.ctrlKey || event.shiftKey) {
    const link = document.createElement('a');
    link.href = handleBasename(target, baseUrl);
    const newEvent = new MouseEvent('click', {
      metaKey: event.metaKey,
      altKey: event.altKey,
      ctrlKey: event.ctrlKey,
      shiftKey: event.shiftKey,
    });
    link.dispatchEvent(newEvent);

    return true;
  }

  return false;
}

export function emulateNativeClick(target: string, event: MouseEvent) {
  return emulateNativeClickManual(target, {
    metaKey: event.metaKey,
    altKey: event.altKey,
    ctrlKey: event.ctrlKey,
    shiftKey: event.shiftKey,
  });
}

export function emulateNativeMiddleClick(target: string) {
  if (
    navigator.userAgent.includes('Firefox') ||
    /^((?!chrome|android).)*safari/i.test(navigator.userAgent)
  ) {
    return false;
  }

  const link = document.createElement('a');
  link.href = handleBasename(target, baseUrl);
  const newEvent = new MouseEvent('click', { button: 1, which: 2 });
  link.dispatchEvent(newEvent);
  return true;
}

const SIZING_SCHEMA = z.record(z.number());

export function useDataGridSizingLocalStorage(id: string) {
  const [columnSizing, _setColumnSizing] = useState<ColumnSizingState>(() => {
    try {
      return SIZING_SCHEMA.parse(
        JSON.parse(window.localStorage.getItem(`ls:dataGridSizing:${id}`) ?? '')
      );
    } catch {
      // pass
    }
    return {};
  });

  const setColumnSizing = (
    sizing:
      | ColumnSizingState
      | ((value: ColumnSizingState) => ColumnSizingState)
  ) => {
    const newColumnSizing =
      typeof sizing === 'function' ? sizing(columnSizing) : sizing;

    window.localStorage.setItem(
      `ls:dataGridSizing:${id}`,
      JSON.stringify(newColumnSizing)
    );

    _setColumnSizing(newColumnSizing);
  };

  return [columnSizing, setColumnSizing] as const;
}
